<template>
  <HrPageLayout
    title="Monthly Attendance Report"
    subtitle="Attendance dashboard by month"
    icon="📅"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Leave & Attendance' },
      { label: 'Monthly Attendance Report' }
    ]"
  >
    <template #tabs>
      <ReportTabs />
    </template>
    <template #header-actions>
      <button
        type="button"
        class="hr-btn hr-btn-primary"
        @click="exportToExcel"
        :disabled="employees.length === 0"
      >
        <i class="bx bx-file"></i> Export
      </button>
    </template>

    <div class="report-monthly-content">
      <!-- Data availability warning -->
      <div v-if="summary.period?.message" class="hr-alert hr-alert-warning hr-mb-4" role="alert">
        <i class="bx bx-error-circle"></i>
        <div>
          <strong>Data Notice:</strong> {{ summary.period.message }}
          <span v-if="!summary.hasAttendanceRecords">
            You may need to import historical data or verify your system start date.
          </span>
        </div>
      </div>

      <!-- Stats cards -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-calendar"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Working Days</p>
          <h2 class="hr-metric-value">{{ summary.totalWorkingDays || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-user-check"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Days Present</p>
          <h2 class="hr-metric-value">{{ summary.totalPresent || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-user-x"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Absences</p>
          <h2 class="hr-metric-value">{{ summary.totalAbsent || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-calendar-heart"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Leave Days</p>
          <h2 class="hr-metric-value">{{ summary.totalLeave || 0 }}</h2>
            </div>
        </div>
      </div>

      <!-- Additional stats for data completeness -->
      <div v-if="summary.totalNoData > 0" class="hr-chart-card hr-mb-4 hr-border-warning">
        <div class="hr-chart-body hr-py-3">
          <div class="hr-flex align-items-center">
            <i class="bx bx-info-circle hr-text-warning hr-me-3" style="font-size: 24px;"></i>
            <div>
              <h6 class="hr-mb-1">Data Completeness</h6>
              <p class="hr-mb-0 hr-text-muted">
                {{ summary.totalNoData }} day(s) have no attendance records.
                This might indicate missing data or dates before system implementation.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Legend -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body hr-py-2">
          <div class="hr-legend-wrap">
            <span class="hr-legend-label"><strong>Legend:</strong></span>
            <div class="hr-legend-badges">
              <span class="hr-badge hr-badge-success">P - Present</span>
              <span class="hr-badge hr-badge-danger">A - Absent</span>
              <span class="hr-badge hr-badge-info">L - Leave</span>
              <span class="hr-badge att-legend-half-leave">HL - Half-day leave</span>
              <span class="hr-badge hr-badge-warning">H - Holiday (Kenya)</span>
              <span class="hr-badge hr-badge-secondary">WO - Weekend/Off</span>
              <span class="hr-badge hr-badge-primary">WP - Weekend Present</span>
              <span class="hr-badge hr-badge-secondary">WL - Weekend Leave</span>
              <span class="hr-badge hr-badge-muted">- - No Data</span>
              <span class="hr-badge hr-badge-muted">NA - Not Applicable</span>
            </div>
            <small class="hr-text-muted hr-d-block hr-mt-2">Working days and absent/present exclude Kenya holidays (synced with Holiday Calendar). Half-day leave (HL) = half present, half leave; counts as 0.5 present.</small>
          </div>
        </div>
      </div>

      <!-- Attendance table -->
      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-table"></i> Monthly Attendance</h5>
        </div>
        <div class="hr-chart-body">
          <div class="hr-form-grid hr-mb-4">
            <div class="hr-filter-group">
              <label class="hr-form-label">Department</label>
              <select v-model="selectedDepartment" class="hr-select" @change="getAttendanceData">
                <option value="">All Departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                  {{ dept.departmentName }}
                </option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Month</label>
              <input
                v-model="selectedMonth"
                type="month"
                class="hr-input"
                :max="getMaxSelectableMonth()"
                :min="getMinSelectableMonth()"
                @change="onMonthChange"
              >
              <small class="hr-text-muted hr-d-block hr-mt-1">Available: {{ getMinSelectableMonth() }} to {{ getMaxSelectableMonth() }}</small>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Search Employee</label>
              <input
                type="text"
                v-model="searchValue"
                class="hr-input"
                placeholder="Search by name or employee number..."
                @input="onSearchInput"
              >
            </div>
          </div>

          <!-- Loading state -->
          <div v-if="loading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p class="hr-text-muted">Loading attendance data...</p>
          </div>

          <!-- Empty state -->
          <div v-else-if="employees.length === 0" class="hr-empty-state">
            <i class="bx bx-search-alt hr-empty-icon"></i>
            <h5>No employees found</h5>
            <p class="hr-text-muted">
              {{ summary.period?.message || "Try adjusting your search or filters" }}
            </p>
          </div>

          <!-- Attendance table -->
          <div v-else class="hr-table-wrapper hr-overflow-auto">
            <table class="hr-table hr-table-bordered att-monthly-table">
              <thead>
                <tr>
                  <th class="att-sticky-col att-sticky-header">Employee<br><small class="hr-text-muted">Join Date</small></th>
                  <th v-for="(day, index) in days" :key="index"
                      :class="['att-day-header', { 'att-weekend-header': isWeekend(day), 'att-current-day': isCurrentDay(day) }]">
                    {{ formatDay(day) }}
                    <span v-if="isCurrentDay(day)" class="hr-badge hr-badge-primary hr-ms-1">Today</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="employee in employees" :key="employee.employeeId" class="att-employee-row">
                  <td class="att-sticky-col att-sticky-cell">
                    <strong>{{ employee.employeeName }}</strong>
                    <br>
                    <small class="hr-text-muted">
                      {{ employee.dateJoined ? formatJoinDate(employee.dateJoined) : 'Join date not available' }}
                    </small>
                  </td>
                  <td v-for="(day, index) in days" :key="index"
                      :class="['att-cell', 'hr-text-center', getAttendanceClass(employee.attendanceByDay[day], day, employee.dateJoined)]"
                      :title="getAttendanceTooltip(employee.attendanceByDay[day], day, employee.dateJoined)">
                    {{ getDisplayStatus(employee.attendanceByDay[day], day) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="summary.totalPages > 1" class="hr-pagination-wrap hr-mt-4">
            <div class="hr-pagination-info">Showing {{ employees.length }} of {{ summary.totalEmployees }} employees</div>
            <nav class="hr-pagination">
              <button type="button" class="hr-page-btn" :disabled="currentPage <= 1" @click="changePage(currentPage - 1)">
                <i class="bx bx-chevron-left"></i>
              </button>
              <template v-for="page in paginationPages" :key="'page-'+page">
                <button v-if="page !== '...'" type="button" class="hr-page-btn"
                        :class="{ 'hr-page-active': page === currentPage }"
                        :disabled="page === '...'"
                        @click="changePage(page)">{{ page }}</button>
                <span v-else class="hr-page-ellipsis">...</span>
              </template>
              <button type="button" class="hr-page-btn" :disabled="currentPage >= summary.totalPages"
                      @click="changePage(currentPage + 1)">
                <i class="bx bx-chevron-right"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ReportTabs from "@/components/reports/ReportTabs.vue";
import { authUrl, execute } from "@/api";
import { buildBrandedHeaderRows, getCompanyName } from "@/utils/reportExportUtils";
import _ from 'lodash';
import * as XLSX from 'xlsx';

export default {
  name: "ReportMonthly",
  components: { HrPageLayout, ReportTabs },
  data() {
    return {
      employees: [],
      days: [],
      departments: [],
      summary: {
        totalEmployees: 0,
        totalPresent: 0,
        totalAbsent: 0,
        totalLeave: 0,
        totalNoData: 0,
        totalWorkingDays: 0,
        totalPages: 1,
        hasData: false,
        hasAttendanceRecords: false,
        period: {
          month: "",
          totalDays: 0,
          message: ""
        }
      },
      searchValue: "",
      selectedDepartment: "",
      selectedMonth: this.getCurrentMonth(),
      currentPage: 1,
      pageSize: 20,
      loading: false,
      systemStartDate: "2023-01" // Adjust this to match your actual system start date
    };
  },
  computed: {
    paginationPages() {
      const pages = [];
      const totalPages = this.summary.totalPages;

      if (totalPages <= 5) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        if (this.currentPage <= 3) {
          pages.push(2, 3);
          if (totalPages > 3) pages.push('...');
        } else if (this.currentPage >= totalPages - 2) {
          pages.push('...', totalPages - 2, totalPages - 1);
        } else {
          pages.push('...', this.currentPage - 1, this.currentPage, this.currentPage + 1, '...');
        }
        if (!pages.includes(totalPages)) {
          pages.push(totalPages);
        }
      }
      return pages;
    },
    selectedDepartmentName() {
      if (!this.selectedDepartment) return 'All Departments';
      const dept = this.departments.find(d => String(d.id) === String(this.selectedDepartment));
      return dept ? dept.departmentName : 'All Departments';
    },
    headerStats() {
      return [
        { icon: 'bx bx-calendar', label: 'Working Days', value: this.summary.totalWorkingDays || 0 },
        { icon: 'bx bx-user-check', label: 'Present', value: this.summary.totalPresent || 0 },
        { icon: 'bx bx-user-x', label: 'Absent', value: this.summary.totalAbsent || 0 },
        { icon: 'bx bx-calendar-heart', label: 'Leave', value: this.summary.totalLeave || 0 }
      ];
    }
  },
  mounted() {
    this.getDepartments();
    this.getAttendanceData();
  },
  methods: {
    getCurrentMonth() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      return `${year}-${month}`;
    },

    getMaxSelectableMonth() {
      return this.getCurrentMonth();
    },

    getMinSelectableMonth() {
      return this.systemStartDate;
    },

    validateSelectedMonth() {
      const selected = new Date(this.selectedMonth + '-01');
      const current = new Date();
      current.setDate(1); // Set to first day of current month

      const systemStart = new Date(this.systemStartDate + '-01');

      if (selected > current) {
        this.selectedMonth = this.getCurrentMonth();
        return false;
      }

      if (selected < systemStart) {
        this.showToast(`Data not available before ${this.formatMonthYear(this.systemStartDate)}`, "warning");
        this.selectedMonth = this.systemStartDate;
        return false;
      }

      return true;
    },

    formatMonthYear(monthString) {
      const date = new Date(monthString + '-01');
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    },

    onMonthChange() {
      if (this.validateSelectedMonth()) {
        this.currentPage = 1;
        this.getAttendanceData();
      }
    },

    formatDay(dayString) {
      const date = new Date(dayString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('default', { month: 'short' });
      const dayOfWeek = date.toLocaleString('default', { weekday: 'short' });
      return `${day} ${month} (${dayOfWeek})`;
    },

    formatJoinDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    isCurrentDay(dayString) {
      const today = new Date();
      const date = new Date(dayString);
      return today.getDate() === date.getDate() &&
          today.getMonth() === date.getMonth() &&
          today.getFullYear() === date.getFullYear();
    },

    isWeekend(dayString) {
      const date = new Date(dayString);
      const day = date.getDay();
      return day === 0 || day === 6;
    },

    wasEmployeeActiveOnDate(joinDate, checkDate) {
      if (!joinDate) return true;
      return new Date(checkDate) >= new Date(joinDate);
    },

    getAttendanceClass(status, day, joinDate) {
      const isWeekend = this.isWeekend(day);
      const wasActive = this.wasEmployeeActiveOnDate(joinDate, day);

      if (!wasActive) {
        return 'att-pre-join';
      }

      if (isWeekend) {
        switch(status) {
          case 'P':
            return 'att-weekend-present';
          case 'L':
            return 'att-weekend-leave';
          case 'A':
            return 'att-weekend-absent';
          case 'WO':
          default:
            return 'att-weekend-off';
        }
      } else {
        switch(status) {
          case 'P':
            return 'att-present';
          case 'A':
            return 'att-absent';
          case 'L':
            return 'att-leave';
          case 'HL':
            return 'att-half-leave';
          case 'H':
            return 'att-holiday';
          case '-':
            return 'att-nodata';
          case 'NA':
            return 'att-pre-join';
          default:
            return 'att-nodata';
        }
      }
    },

    getAttendanceTooltip(status, day, joinDate) {
      const isWeekend = this.isWeekend(day);
      const wasActive = this.wasEmployeeActiveOnDate(joinDate, day);

      if (!wasActive) {
        return 'Employee had not joined yet on this date';
      }

      if (isWeekend) {
        switch(status) {
          case 'P':
            return 'Present on Weekend - Outstanding Work!';
          case 'L':
            return 'Leave on Weekend';
          case 'A':
            return 'Absent on Weekend';
          case 'WO':
          default:
            return 'Weekend/Day Off';
        }
      } else {
        switch(status) {
          case 'P':
            return 'Present';
          case 'A':
            return 'Absent';
          case 'L':
            return 'Leave';
          case 'HL':
            return 'Half-day leave (half present, half leave)';
          case 'H':
            return 'Kenya Holiday (not counted absent)';
          case '-':
            return 'No attendance record available';
          case 'NA':
            return 'Employee had not joined yet';
          default:
            return status;
        }
      }
    },

    getDisplayStatus(status, day) {
      const isWeekend = this.isWeekend(day);

      if (isWeekend) {
        switch(status) {
          case 'P':
            return 'WP'; // Weekend Present
          case 'L':
            return 'WL'; // Weekend Leave
          case 'A':
            return 'WA'; // Weekend Absent
          case 'WO':
          default:
            return 'WO'; // Weekend Off
        }
      } else {
        if (status === 'H') return 'H';
        if (status === 'HL') return 'HL';
        return status || '-';
      }
    },

    onSearchInput: _.debounce(function() {
      this.currentPage = 1;
      this.getAttendanceData();
    }, 500),

    showToast(message, type = 'info') {
      if (this.$toast) {
        this.$toast[type](message);
      } else {
        // Fallback for when toast is not available
        console.log(`${type.toUpperCase()}: ${message}`);
        alert(message);
      }
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success && res.data.data?.departments) {
              this.departments = res.data.data.departments;
            }
          })
          .catch((e) => {
            console.error("Error fetching departments:", e);
            this.showToast("Failed to load departments", "error");
          });
    },

    getAttendanceData() {
      if (!this.validateSelectedMonth()) {
        return;
      }

      this.loading = true;
      const data = new FormData();
      data.append("function", "getMonthlyAttendanceDashboard");
      data.append("page", this.currentPage);
      data.append("rows_per_page", this.pageSize);
      data.append("selectedMonth", this.selectedMonth);
      data.append("search", this.searchValue);

      if (this.selectedDepartment) {
        data.append("departmentId", this.selectedDepartment);
      }

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.employees = res.data.data.employees || [];
              this.days = res.data.data.days || [];
              this.summary = {
                ...this.summary,
                ...res.data.data.summary
              };

            } else {
              console.error("Error:", res.data.message);
              this.showToast(res.data.message || "Failed to load attendance data", "error");
            }
            this.loading = false;
          })
          .catch((e) => {
            console.error("API error:", e);
            this.showToast("Network error while loading attendance data", "error");
            this.loading = false;
          });
    },

    changePage(page) {
      if (page < 1 || page > this.summary.totalPages || page === this.currentPage) {
        return;
      }
      this.currentPage = page;
      this.getAttendanceData();
    },

    exportToExcel() {
      if (this.employees.length === 0) {
        this.showToast("No data to export", "warning");
        return;
      }

      try {
        const workbook = XLSX.utils.book_new();

        // Branded header rows
        const brandedRows = buildBrandedHeaderRows({
          companyName: getCompanyName(),
          reportTitle: 'Monthly Attendance Report',
          period: this.selectedMonth,
          department: this.selectedDepartmentName
        });

        // Create headers
        const headers = ['Employee', 'Join Date'];
        this.days.forEach(day => {
          headers.push(this.formatDay(day));
        });

        // Create data with branded header
        const attendanceData = [...brandedRows, headers];

        this.employees.forEach(employee => {
          const row = [
            employee.employeeName,
            this.formatJoinDate(employee.dateJoined)
          ];
          this.days.forEach(day => {
            const status = employee.attendanceByDay[day];
            const displayStatus = this.getDisplayStatus(status, day);
            row.push(displayStatus || '-');
          });
          attendanceData.push(row);
        });

        // Add summary sheet with branded header
        const summaryData = [
          ...buildBrandedHeaderRows({ companyName: getCompanyName(), reportTitle: 'Monthly Attendance Summary', period: this.selectedMonth }),
          ['Period', this.summary.period.month],
          ['Total Employees', this.summary.totalEmployees],
          ['Total Working Days', this.summary.totalWorkingDays],
          ['Total Present', this.summary.totalPresent],
          ['Total Absent', this.summary.totalAbsent],
          ['Total Leave', this.summary.totalLeave],
          ['Missing Data Days', this.summary.totalNoData],
          ['Weekend Work', this.summary.totalWeekendWork],
          ['Weekend Leave', this.summary.totalWeekendLeave]
        ];

        const attendanceSheet = XLSX.utils.aoa_to_sheet(attendanceData);
        const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);

        XLSX.utils.book_append_sheet(workbook, attendanceSheet, 'Attendance Report');
        XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

        const fileName = `Attendance_Report_${this.selectedMonth}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        this.showToast("Excel report downloaded successfully", "success");
      } catch (error) {
        console.error("Error generating Excel file:", error);
        this.showToast("Failed to generate Excel report", "error");
      }
    }
  },
  watch: {
    selectedDepartment() {
      this.currentPage = 1;
      this.getAttendanceData();
    }
  }
};
</script>

<style src="@/assets/css/hr-page-shared.css"></style>
<style scoped>
.hr-alert { display: flex; align-items: flex-start; gap: 12px; padding: 16px; border-radius: 8px; margin-bottom: 20px; }
.hr-alert i { flex-shrink: 0; font-size: 20px; margin-top: 2px; }
.hr-alert-warning { background: rgba(255,193,7,0.15); border-left: 4px solid #ffc107; }
.hr-border-warning { border-left: 4px solid #ffc107; }
.hr-flex { display: flex; }
.hr-legend-wrap { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.hr-legend-label { margin-right: 12px; }
.hr-legend-badges { display: flex; flex-wrap: wrap; gap: 8px; }
.hr-d-block { display: block; }
.hr-me-3 { margin-right: 12px; }
.hr-mt-2 { margin-top: 8px; }
.hr-mb-1 { margin-bottom: 4px; }
.hr-ms-1 { margin-left: 4px; }
.hr-py-3 { padding-top: 12px; padding-bottom: 12px; }
.hr-loading-wrap { text-align: center; padding: 3rem; }
.hr-spinner { width: 40px; height: 40px; border: 3px solid rgba(91,115,232,0.2); border-top-color: #5b73e8; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 1rem; }
@keyframes spin { to { transform: rotate(360deg); } }
.hr-empty-state { text-align: center; padding: 3rem; }
.hr-empty-icon { font-size: 48px; color: #94a3b8; margin-bottom: 1rem; display: block; }
.hr-overflow-auto { overflow-x: auto; }
.hr-table-bordered th, .hr-table-bordered td { border: 1px solid #e2e8f0; padding: 8px; }

/* Attendance table sticky columns */
.att-sticky-col { position: sticky; background: #fff; z-index: 1; }
.att-sticky-header { left: 0; z-index: 101; background: #f8fafc; }
.att-sticky-cell { left: 0; z-index: 99; min-width: 180px; }
.att-day-header { min-width: 70px; white-space: nowrap; }
.att-weekend-header { background: #fff8e8 !important; }
.att-current-day { position: relative; }
.att-current-day::after { content: ""; position: absolute; top: 0; left: 0; right: 0; bottom: 0; border: 2px solid #5b73e8; pointer-events: none; }
.att-employee-row:hover { background: rgba(0,0,0,0.02); }

/* Attendance cell colors */
.att-cell { font-weight: 600; min-width: 36px; }
.att-present { background: #10b981; color: #fff; }
.att-absent { background: #ef4444; color: #fff; }
.att-leave { background: #06b6d4; color: #fff; }
.att-half-leave {
  background: linear-gradient(135deg, #3b82f6 0%, #10b981 100%);
  color: #fff;
  font-weight: 600;
}
.att-legend-half-leave {
  background: linear-gradient(135deg, #3b82f6 0%, #10b981 100%);
  color: #fff;
  border: none;
}
.att-holiday { background: #f59e0b; color: #1e293b; }
.att-nodata { background: #f1f5f9; color: #64748b; }
.att-pre-join { background: #94a3b8; color: #fff; opacity: 0.6; }
.att-weekend-present { background: #3b82f6; color: #fff; }
.att-weekend-leave { background: #64748b; color: #fff; }
.att-weekend-absent { background: #1e293b; color: #fff; }
.att-weekend-off { background: #fef3c7; color: #92400e; }

@media (max-width: 768px) {
  .att-monthly-table { font-size: 0.8rem; }
  .att-sticky-cell { min-width: 140px; }
}
</style>